package er.prateek.crossly.notifications

import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.notif_daily_challenge_body
import crossly.shared.generated.resources.notif_daily_challenge_title
import crossly.shared.generated.resources.notif_reengagement_body
import crossly.shared.generated.resources.notif_reengagement_title
import crossly.shared.generated.resources.notif_streak_at_risk_body
import crossly.shared.generated.resources.notif_streak_at_risk_title
import crossly.shared.generated.resources.notif_streak_count_body
import crossly.shared.generated.resources.notif_streak_restore_body
import crossly.shared.generated.resources.notif_streak_restore_title
import er.prateek.crossly.config.AppConfig
import er.prateek.crossly.config.NotificationsConfig
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.firebase.AnalyticsLogger
import er.prateek.crossly.firebase.RemoteConfigClient
import er.prateek.crossly.firebase.RemoteConfigKeys
import er.prateek.crossly.persistence.NotificationPrefsStore
import er.prateek.crossly.progression.DailyPuzzleManager
import er.prateek.crossly.progression.StreakManager
import kotlin.time.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.getString

/**
 * The decision-making brain behind the smart notifications feature. Owns nothing about *how* a
 * reminder is shown (that's [LocalNotificationScheduler]) or *whether the OS allows it*
 * (that's [NotificationPermissionController]) -- only *when* and *what*.
 *
 * All config is re-read from [remoteConfig] on every call rather than cached at construction,
 * matching the rest of the app's "config can change between launches" convention.
 */
class NotificationCoordinator(
    private val prefsStore: NotificationPrefsStore,
    private val dailyPuzzleManager: DailyPuzzleManager,
    private val streakManager: StreakManager,
    private val remoteConfig: RemoteConfigClient,
    private val permission: NotificationPermissionController,
    private val scheduler: LocalNotificationScheduler,
    private val analytics: AnalyticsLogger,
    private val sessionCount: () -> Int,
    private val puzzlesCompleted: () -> Int,
    private val clock: Clock = Clock.System,
) {
    private fun config(): NotificationsConfig =
        AppConfig.parse(remoteConfig.getString(RemoteConfigKeys.APP_CONFIG)).notifications

    /**
     * Called once per cold start, after [RemoteConfigClient.warmUp] -- records this open as a
     * personalization sample and, if everything is green-lit, (re)schedules tomorrow's reminder.
     */
    suspend fun onAppOpened() {
        val config = config()
        val now = clock.now().toLocalDateTime(TimeZone.currentSystemDefault())
        prefsStore.recordAppOpen(now.hour * 60 + now.minute, config.openHistoryWindowSize)
        scheduleIfChanged(config)
    }

    /**
     * A game was finished or a streak restored: the reminder's story may have changed (e.g. the
     * streak went from "at risk" to "safe", or a restore offer appeared/vanished), so re-pick it.
     */
    suspend fun onProgressChanged() {
        scheduleIfChanged(config())
    }

    private suspend fun scheduleIfChanged(config: NotificationsConfig) {
        if (!scheduler.isSupported || !config.enabled || !prefsStore.remindersEnabledByUser()) {
            return
        }
        if (permission.currentStatus() != NotificationPermissionStatus.GRANTED) return

        val variant = pickVariant(config) ?: return
        val (hour, minute) = OpenTimePersonalizer.reminderHourMinute(prefsStore.openTimeHistory(), config)

        // Reschedule only when something actually changed, so an active player doesn't churn
        // the same work/notification request on every single launch. Streak copy carries the day
        // count, so the count is part of the key -- otherwise iOS would keep showing a stale number.
        val scheduleKey = when (variant) {
            ReminderVariant.STREAK_AT_RISK, ReminderVariant.STREAK_RESTORE -> "${variant.name}:${streakManager.streak.current}"
            else -> variant.name
        }
        if (prefsStore.lastScheduledVariant() == scheduleKey && prefsStore.lastScheduledHour() == hour) return

        scheduler.ensureChannel()
        val content = resolveContent(variant)
        scheduler.scheduleDailyReminder(hour, minute, content)
        prefsStore.recordScheduled(scheduleKey, hour)
        analytics.logEvent(
            AnalyticsEvents.NOTIFICATION_SCHEDULED,
            mapOf("variant" to variant.name, "hour" to hour, "personalized" to (prefsStore.openTimeHistory().size >= config.minOpenHistoryForPersonalization)),
        )
    }

    /**
     * Picks the highest-priority variant still enabled by config, or `null` if none apply.
     *
     * [atDelivery] is true when called right before the reminder is shown (Android's worker): a
     * player who already played today is then left alone rather than nagged. When scheduling
     * ahead, the streak story is chosen even if today is played, since the next fire is usually
     * tomorrow, when it will be at risk again.
     */
    private fun pickVariant(config: NotificationsConfig, atDelivery: Boolean = false): ReminderVariant? {
        val dailyDoneToday = dailyPuzzleManager.hasCompletedToday()
        val playedToday = streakManager.playedToday()
        if (atDelivery && playedToday) return null
        return when {
            config.streakRemindersEnabled && streakManager.restoreOffer() != null -> ReminderVariant.STREAK_RESTORE
            config.streakRemindersEnabled && streakManager.displayStreak() > 0 -> ReminderVariant.STREAK_AT_RISK
            config.dailyChallengeRemindersEnabled && !dailyDoneToday -> ReminderVariant.DAILY_CHALLENGE_AVAILABLE
            config.reengagementRemindersEnabled -> ReminderVariant.REENGAGEMENT
            else -> null
        }
    }

    private suspend fun resolveContent(variant: ReminderVariant): ReminderContent {
        val (title, body) = when (variant) {
            ReminderVariant.STREAK_RESTORE ->
                getString(Res.string.notif_streak_restore_title, streakManager.streak.current) to
                    getString(Res.string.notif_streak_restore_body)
            ReminderVariant.STREAK_AT_RISK -> {
                val days = streakManager.streak.current
                getString(Res.string.notif_streak_at_risk_title) to
                    if (days > 1) getString(Res.string.notif_streak_count_body, days) else getString(Res.string.notif_streak_at_risk_body)
            }
            ReminderVariant.DAILY_CHALLENGE_AVAILABLE ->
                getString(Res.string.notif_daily_challenge_title) to getString(Res.string.notif_daily_challenge_body)
            ReminderVariant.REENGAGEMENT ->
                getString(Res.string.notif_reengagement_title) to getString(Res.string.notif_reengagement_body)
        }
        return ReminderContent(variant, title, body)
    }

    /**
     * Re-derives fresh content for whatever the *current* state calls for, ignoring
     * [prefsStore]'s last-scheduled bookkeeping. Used by Android's delivery-time worker, which
     * can run real code right before showing a notification -- see the androidMain actual of
     * [LocalNotificationScheduler] for why iOS can't do the same.
     */
    suspend fun resolveContentForDelivery(): ReminderContent? {
        val config = config()
        if (!config.enabled || !prefsStore.remindersEnabledByUser()) return null
        val variant = pickVariant(config, atDelivery = true) ?: return null
        return resolveContent(variant)
    }

    /** Gate for the native OS prompt -- called from a "moment of investment" (first result screen). */
    suspend fun shouldRequestOsPermission(): Boolean {
        val config = config()
        return config.enabled &&
            prefsStore.remindersEnabledByUser() &&
            !prefsStore.hasRequestedOsPermission() &&
            permission.currentStatus() == NotificationPermissionStatus.NOT_DETERMINED &&
            sessionCount() >= config.permissionAskMinSessions &&
            puzzlesCompleted() >= config.permissionAskMinPuzzlesCompleted
    }

    suspend fun requestOsPermission() {
        prefsStore.markOsPermissionRequested()
        analytics.logEvent(AnalyticsEvents.NOTIFICATION_PERMISSION_REQUESTED)
        val result = permission.requestPermission()
        analytics.logEvent(
            if (result == NotificationPermissionStatus.GRANTED) {
                AnalyticsEvents.NOTIFICATION_PERMISSION_GRANTED
            } else {
                AnalyticsEvents.NOTIFICATION_PERMISSION_DENIED
            },
        )
    }

    /** Gate for the themed re-ask sheet, shown after a denial instead of nagging with the OS dialog. */
    suspend fun shouldShowSoftAsk(): Boolean {
        val config = config()
        if (!config.enabled || !prefsStore.remindersEnabledByUser()) return false
        if (permission.currentStatus() != NotificationPermissionStatus.DENIED) return false
        if (prefsStore.softAskCount() >= config.maxSoftAsks) return false
        val lastShown = prefsStore.lastSoftAskEpochDay() ?: return true
        val todayEpochDay = clock.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toEpochDays()
        return todayEpochDay - lastShown >= config.softAskCooldownDays
    }

    fun onSoftAskShown() {
        val todayEpochDay = clock.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toEpochDays()
        prefsStore.recordSoftAskShown(todayEpochDay)
        analytics.logEvent(AnalyticsEvents.NOTIFICATION_SOFT_ASK_SHOWN)
    }

    fun onSoftAskSettingsTapped() {
        analytics.logEvent(AnalyticsEvents.NOTIFICATION_SOFT_ASK_SETTINGS_TAPPED)
        permission.openAppSettings()
    }

    fun onSoftAskDismissed() {
        analytics.logEvent(AnalyticsEvents.NOTIFICATION_SOFT_ASK_DISMISSED)
    }

    fun setRemindersEnabledByUser(enabled: Boolean) {
        val from = prefsStore.remindersEnabledByUser()
        prefsStore.setRemindersEnabledByUser(enabled)
        analytics.logEvent(AnalyticsEvents.NOTIFICATIONS_TOGGLED, mapOf("from" to from, "to" to enabled))
        if (!enabled) {
            scheduler.cancelDailyReminder()
            analytics.logEvent(AnalyticsEvents.NOTIFICATION_CANCELLED, mapOf("reason" to "user_disabled"))
        }
    }
}
